import { createSlice, createSelector, PayloadAction } from '@reduxjs/toolkit'
import { ApiMiddlewarePayload } from '@ocean/api'
import { CommonUtils } from '@ocean/utils'
import { AppThunk } from 'store'
import { ApiAction, MiddlewareApiAction, createApiAction } from 'api'
import { userLoggedOut } from 'core/session/slices/session'
import { getListInstructorLedEnrollments } from 'modules/simulatorTrainings/common/api/simulatorTrainingService'
import { listInstructorLedEnrollmentsLoaded } from 'modules/simulatorTrainings/common/slices/instructor'
import { SimulatorTrainingPersonType } from 'modules/simulatorTrainings/common/types'
import { SimulatorInstructorLedEnrolledPerson } from 'modules/simulatorTrainings/common/types/instructor'
import { assessSimulatorTrainingScheduleParticipants } from 'modules/simulatorTrainings/instructor/instructorLed/TrainingDetails/api/trainingDetailsService'
import { AssessmentRecord } from 'modules/simulatorTrainings/instructor/instructorLed/TrainingDetails/types'
import { TrainingDetailsState, ApiAssessParticipantsResponse } from './types'

const initialState: TrainingDetailsState = {
  selectedAssessments: {},
  assessParticipantsRequest: {},
}

const trainingDetailsSlice = createSlice({
  name: 'simulatorTrainingInstructorILTrainingDetails',
  initialState,
  reducers: {
    assessScheduleParticipantsRequested(
      state,
      action: PayloadAction<ApiMiddlewarePayload<ApiAssessParticipantsResponse>>
    ) {
      state.assessParticipantsRequest = action.payload
    },
    changeAssessments(state, action: PayloadAction<AssessmentRecord>) {
      state.selectedAssessments = action.payload
    },
    clearSelectedAssessments(state) {
      state.selectedAssessments = initialState.selectedAssessments
    },
  },
  extraReducers: builder => {
    builder.addCase(userLoggedOut, () => {
      return initialState
    })
  },
})

const { changeAssessments, clearSelectedAssessments, assessScheduleParticipantsRequested } =
  trainingDetailsSlice.actions
export { changeAssessments, clearSelectedAssessments }
export default trainingDetailsSlice.reducer

export const getEnrollmentsPartition = createSelector(
  (enrollments: SimulatorInstructorLedEnrolledPerson[] = []) => enrollments,
  enrollments =>
    CommonUtils.partition<SimulatorInstructorLedEnrolledPerson>(
      enrollments,
      (person: SimulatorInstructorLedEnrolledPerson) =>
        person.personType === SimulatorTrainingPersonType.Instructor
    )
)

export const cleanUpEnrollmentsChanges =
  (response: ApiMiddlewarePayload): AppThunk =>
  async dispatch => {
    dispatch(listInstructorLedEnrollmentsLoaded(response))
    if (!response.error) {
      dispatch(clearSelectedAssessments())
    }
  }

export const reloadListInstructorLedEnrollmentsData = (scheduleId: Guid): ApiAction => {
  return createApiAction({
    apiCalls: {
      list: () => getListInstructorLedEnrollments(scheduleId),
    },
    payloadAction: listInstructorLedEnrollmentsLoaded,
    nextAction: cleanUpEnrollmentsChanges,
    getResultToKeep: ({ simulatorTrainingInstructor }) =>
      simulatorTrainingInstructor.listInstructorLedEnrollmentsData.result,
  })
}

export const getInitialAssessments = createSelector(
  (students: SimulatorInstructorLedEnrolledPerson[] = []) => students,
  students => {
    const result: AssessmentRecord = {}
    students.forEach(({ userId, status }: SimulatorInstructorLedEnrolledPerson) => {
      result[userId] = status
    })
    return result
  }
)

export const updateListInstructorLedEnrollments =
  (response: ApiMiddlewarePayload, params: { scheduleId: Guid }): AppThunk =>
  async dispatch => {
    const { scheduleId } = params
    dispatch(assessScheduleParticipantsRequested(response))
    if (!response.error) {
      dispatch(reloadListInstructorLedEnrollmentsData(scheduleId) as MiddlewareApiAction)
    }
  }

export const assessScheduleParticipants = (
  scheduleId: Guid,
  selectedAssessments: AssessmentRecord
): ApiAction => {
  return createApiAction({
    apiCalls: {
      request: () => assessSimulatorTrainingScheduleParticipants(scheduleId, selectedAssessments),
    },
    payloadAction: assessScheduleParticipantsRequested,
    nextAction: {
      action: updateListInstructorLedEnrollments,
      params: { scheduleId },
    },
    shouldShowGlobalLoading: true,
    enableErrorNotification: true,
  })
}
